import React, { useState } from 'react';
import { Button, Group, Badge } from '@mantine/core';
import { toast } from '../../ui/toast';
import { IconWand, IconSearch } from '@tabler/icons-react';
import OptimizationModal from './OptimizationModal';
import { useOptimization } from '../../hooks/useOptimization';
import { OptimizationService } from '../../services/optimization/OptimizationService';
import { OPTIMIZATION_STATUS } from '../../services/optimization/constants';
import OptimizationResultsModal from './OptimizationResultsModal';
import OptimizationHistoryModal from './OptimizationHistoryModal';
import { useTranslation } from 'react-i18next';

export default function OptimizeEntry({
  planningId,
  ownerId,
  isOwner,
  assignableUsers,
  user,
  events,
  optimizationProfile = 'default',
  optimizationOverrides = {},
  onLocalAssignmentsChange,
}) {
  const { t } = useTranslation('common');
  const [modalOpen, setModalOpen] = useState(false);
  const { status, hasActiveStatus, startOptimization, refresh, currentRun } = useOptimization(planningId);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultPayload, setResultPayload] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reviewRun, setReviewRun] = useState(null);
  // no loading state needed since we open history modal directly now

  const hasRuns = Boolean(currentRun?.id);

  const onRun = async (optimizationFile) => {
    try {
      await startOptimization({ optimizationFile, createdBy: user?.uid });
      setModalOpen(false);
    } catch (e) {
      toast.show({ title: t('optimization.error_title', { defaultValue: 'Optimization error' }), message: String(e?.message || e), color: 'red' });
    }
  };

  // Owner/admin only (support multi-owner via prop)
  const canManage = (typeof isOwner === 'boolean') ? isOwner : (user?.uid && ownerId && user.uid === ownerId);
  if (!canManage) return null;

  // Keep previous logic when a run is queued/running/failed
  if (hasActiveStatus) {
    const label = status === OPTIMIZATION_STATUS.QUEUED
      ? t('optimization.queued', { defaultValue: 'Queued' })
      : t('optimization.running', { defaultValue: 'Running...' });
    return <Button variant="light" disabled>{label}</Button>;
  }

  if (status === OPTIMIZATION_STATUS.FAILED) {
    return (
      <>
        <Group>
          <Badge color="red">{t('optimization.failed', { defaultValue: 'Optimization Failed' })}</Badge>
          <Button leftSection={<IconWand size={16} />} onClick={() => setModalOpen(true)}>
            {t('optimization.optimize', { defaultValue: 'Optimize' })}
          </Button>
        </Group>
        <OptimizationModal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          planningId={planningId}
          ownerId={ownerId}
          assignableUsers={assignableUsers}
          events={events}
          presetKey={optimizationProfile}
          presetOverrides={optimizationOverrides}
          weeklyDefaults={{ internal: Number(optimizationOverrides?.weeklyDefaultInternal) || 40, external: Number(optimizationOverrides?.weeklyDefaultExternal) || 44 }}
          onRun={onRun}
        />
      </>
    );
  }

  // New logic: if no runs yet, show only Optimize; otherwise show both View Results and Optimize
  return (
    <>
      <Group>
        {hasRuns && (
          <Button leftSection={<IconSearch size={16} />} onClick={() => setHistoryOpen(true)}>
            {t('opt_results.view_results', { defaultValue: 'View Results' })}
          </Button>
        )}
        <Button leftSection={<IconWand size={16} />} variant={hasRuns ? 'light' : 'filled'} onClick={() => setModalOpen(true)}>
          {t('optimization.optimize', { defaultValue: 'Optimize' })}
        </Button>
      </Group>

      <OptimizationModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        planningId={planningId}
        ownerId={ownerId}
        assignableUsers={assignableUsers}
        events={events}
        presetKey={optimizationProfile}
        presetOverrides={optimizationOverrides}
        weeklyDefaults={{ internal: Number(optimizationOverrides?.weeklyDefaultInternal) || 40, external: Number(optimizationOverrides?.weeklyDefaultExternal) || 44 }}
        onRun={onRun}
      />

      <OptimizationResultsModal
        opened={resultsOpen}
        onClose={() => setResultsOpen(false)}
        planningId={planningId}
        run={reviewRun || currentRun}
        result={resultPayload}
        assignableUsers={assignableUsers}
        events={events}
        onApply={async (byEvent) => {
          if (!byEvent || !Object.keys(byEvent).length) {
            setResultsOpen(false);
            toast.show({ title: t('opt_results.no_changes_title', { defaultValue: 'No changes' }), message: t('opt_results.no_changes_msg', { defaultValue: 'Nothing to apply from results' }), color: 'gray' });
            return;
          }
          await OptimizationService.setAssignmentsBatch(planningId, byEvent);
          // Optimistic local UI update
          try {
            if (typeof onLocalAssignmentsChange === 'function') {
              // Pass per-role object so the caller can normalize correctly
              Object.entries(byEvent).forEach(([eid, value]) => {
                onLocalAssignmentsChange([eid], value);
              });
            }
          } catch {}
          await refresh();
          setResultsOpen(false);
          toast.show({ title: t('opt_results.applied_title', { defaultValue: 'Assignments applied' }), message: t('opt_results.applied_msg', { defaultValue: 'Optimization assignments have been applied' }), color: 'green' });
        }}
        onCancel={() => {
          setResultsOpen(false);
          toast.show({ title: t('opt_results.canceled_title', { defaultValue: 'Changes canceled' }), message: t('opt_results.canceled_msg', { defaultValue: 'Optimization results were not applied' }), color: 'yellow' });
        }}
      />

      <OptimizationHistoryModal
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
        planningId={planningId}
        onReviewRun={async (run, result) => {
          setReviewRun(run);
          setResultPayload(result);
          setResultsOpen(true);
        }}
        onReapplied={async () => {
          await refresh();
        }}
      />
    </>
  );
}
